import { Checkbox, Modal } from 'antd'
import { Button } from 'common/components/Button/Button'
import { Textarea } from 'common/components/Textarea/Textarea'
import { BUTTON_MODIFIER, BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import type { DrawingElementDescription } from 'features/Procedure/state/procedure.type'
import {
  formatDrawingNoteDisplay,
  normalizeDrawingDescription,
} from 'features/Procedure/utils/drawingDescription.utils'
import { useEffect, useId, useState } from 'react'
import styles from './edit-description-dialog.module.scss'

type Props = {
  isOpen: boolean
  description?: DrawingElementDescription
  unitsNoteMode?: boolean
  onSave: (description: DrawingElementDescription | undefined) => void
  onDelete?: () => void
  onClose: () => void
  readOnly?: boolean
}

export const EditDescriptionDialog = ({
  isOpen,
  description,
  unitsNoteMode = false,
  onSave,
  onDelete,
  onClose,
  readOnly = false,
}: Props) => {
  const [localText, setLocalText] = useState('')
  const [isUnitsChecked, setIsUnitsChecked] = useState(true)
  const unitsNoteCheckboxId = useId()

  const filterNumericOnly = (value: string) => value.replace(/[^\d.,]/g, '')

  useEffect(() => {
    if (!isOpen) return
    const shown = readOnly ? formatDrawingNoteDisplay(description) : undefined
    if (readOnly) {
      setLocalText(shown ?? '')
      setIsUnitsChecked(false)
      return
    }
    const n = normalizeDrawingDescription(description)
    const isNew = description === undefined || description === ''
    const checked = unitsNoteMode ? (isNew ? true : n.isUnitsChecked) : false
    setIsUnitsChecked(checked)
    setLocalText(checked ? filterNumericOnly(n.text) : n.text)
  }, [isOpen, description, readOnly, unitsNoteMode])

  const handleSave = () => {
    if (readOnly) {
      onClose()
      return
    }

    if (unitsNoteMode) {
      const trimmed = localText.trim()
      if (!trimmed && !isUnitsChecked) {
        onSave(undefined)
      } else {
        onSave({ text: localText.trim(), isUnitsChecked })
      }
    } else {
      const trimmed = localText.trim()
      onSave(trimmed ? trimmed : undefined)
    }
    onClose()
  }

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      getContainer={() => (document.fullscreenElement as HTMLElement) || document.body}
      classNames={{
        content: styles.modalContent,
      }}
      closeIcon={null}
      maskClosable={!readOnly}
      title={null}
      footer={null}
      centered
      destroyOnHidden>
      <div className={styles.container}>
        {!readOnly && !unitsNoteMode && <h2 className={styles.dialogTitle}>Edit Description</h2>}
        {readOnly && <h2 className={styles.dialogTitle}>Description</h2>}
        <div className={styles.contentWrapper}>
          <Textarea
            value={localText}
            onChange={(e) => {
              const next = e.target.value
              setLocalText(unitsNoteMode && isUnitsChecked ? filterNumericOnly(next) : next)
            }}
            placeholder={
              unitsNoteMode && isUnitsChecked
                ? 'Enter numeric value only (e.g. 2.5)'
                : 'Type here...This note will SHOW in medical chart.'
            }
            autoSize={{ minRows: 4, maxRows: 10 }}
            readOnly={readOnly}
            className={styles.textArea}
          />
          {!readOnly && unitsNoteMode && (
            <label className={styles.unitsRow} htmlFor={unitsNoteCheckboxId}>
              <Checkbox
                id={unitsNoteCheckboxId}
                checked={isUnitsChecked}
                onChange={(e) => {
                  const checked = e.target.checked
                  setIsUnitsChecked(checked)
                  if (checked) setLocalText(filterNumericOnly(localText))
                }}
                className={styles.unitsCheckbox}
              />
              <span className={styles.unitsLabel}>
                Units note (
                <strong>
                  This will auto add the &quot;units&quot; text, only insert the numeric value.
                  Uncheck when inserting normal note
                </strong>
                )
              </span>
            </label>
          )}
        </div>
        <div className={styles.footer}>
          {readOnly ? (
            <>
              <span />
              <div className={styles.footerActions}>
                <Button onClick={onClose} htmlType='button'>
                  Close
                </Button>
              </div>
            </>
          ) : (
            <>
              {onDelete ? (
                <Button severity={BUTTON_SEVERITY.DANGER} onClick={onDelete} htmlType='button'>
                  Delete
                </Button>
              ) : (
                <span />
              )}
              <div className={styles.footerActions}>
                <Button modifier={BUTTON_MODIFIER.DEFAULT} onClick={onClose} htmlType='button'>
                  Cancel
                </Button>
                <Button onClick={handleSave} htmlType='button'>
                  Save
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
